// Shared editor + renderer for the "video" block / "video page". Used by:
//   • ProposalBuilder     — inline video block (builder flow)
//   • SigningPageBuilder  — intro Video Page for upload-flow proposals
//   • PublicProposal      — client-facing block render
//   • PublicUploadProposal — client-facing intro Video Page render
//
// All visual tokens stay in sync so what the rep sees in the editor is
// exactly what the client sees.

import { useMemo } from "react";
import { Video as VideoIcon, Palette, AlertCircle } from "lucide-react";
import { parseVideoEmbed, VIDEO_BG_PALETTE, isLightColor } from "../lib/videoEmbed";

const PROVIDER_LABELS = {
  youtube: "YouTube", vimeo: "Vimeo", loom: "Loom", wistia: "Wistia",
};

export function VideoBlockEditor({ value, onChange, testIdPrefix = "video" }) {
  const v = value || {};
  const parsed = useMemo(() => parseVideoEmbed(v.embed_url || ""), [v.embed_url]);
  const bg = v.bg_color || "#5B4FE8";
  const layout = v.layout === "video_only" ? "video_only" : "with_text";

  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* URL */}
      <div>
        <label className="block mb-1 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Video URL (YouTube, Vimeo, Loom or Wistia)</label>
        <input
          type="url"
          className="pg-input w-full"
          value={v.embed_url || ""}
          onChange={(e) => set({ embed_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=…"
          data-testid={`${testIdPrefix}-url`}
        />
        {v.embed_url && !parsed && (
          <div className="text-xs mt-1 flex items-center gap-1" style={{ color: "var(--pg-red)" }}>
            <AlertCircle size={11} /> We couldn't recognise that URL — paste the share link from YouTube, Vimeo, Loom or Wistia.
          </div>
        )}
        {parsed && (
          <div className="text-xs mt-1" style={{ color: "var(--pg-text-mute)" }}>
            {PROVIDER_LABELS[parsed.provider] || parsed.provider} · ID {parsed.id}
          </div>
        )}
      </div>

      {/* Layout toggle */}
      <div>
        <label className="block mb-1 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Layout</label>
        <div style={{ display: "inline-flex", padding: 3, borderRadius: 999, background: "var(--pg-surface-2)" }}>
          <SegBtn active={layout === "with_text"}  onClick={() => set({ layout: "with_text" })}  testid={`${testIdPrefix}-layout-with-text`}>Heading + intro</SegBtn>
          <SegBtn active={layout === "video_only"} onClick={() => set({ layout: "video_only" })} testid={`${testIdPrefix}-layout-video-only`}>Video only</SegBtn>
        </div>
      </div>

      {layout === "with_text" && (
        <>
          <div>
            <label className="block mb-1 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Heading</label>
            <input
              className="pg-input w-full"
              value={v.title || ""}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Watch a 90-second walkthrough"
              data-testid={`${testIdPrefix}-title`}
              maxLength={140}
            />
          </div>
          <div>
            <label className="block mb-1 text-[11px]" style={{ color: "var(--pg-text-mute)" }}>Intro text</label>
            <textarea
              className="pg-input w-full"
              rows={3}
              value={v.intro || ""}
              onChange={(e) => set({ intro: e.target.value })}
              placeholder="A quick walkthrough of what's in this proposal."
              data-testid={`${testIdPrefix}-intro`}
              maxLength={1000}
            />
          </div>
        </>
      )}

      {/* Color picker */}
      <div>
        <label className="block mb-1 text-[11px] flex items-center gap-1" style={{ color: "var(--pg-text-mute)" }}>
          <Palette size={11} /> Page background
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {VIDEO_BG_PALETTE.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => set({ bg_color: hex })}
              aria-label={`Use ${hex}`}
              data-testid={`${testIdPrefix}-color-${hex.replace("#", "")}`}
              style={{
                width: 24, height: 24, borderRadius: 6,
                background: hex,
                border: v.bg_color?.toLowerCase() === hex.toLowerCase()
                  ? "2px solid var(--pg-accent)"
                  : "1px solid rgba(0,0,0,0.15)",
                cursor: "pointer",
                boxShadow: isLightColor(hex) ? "inset 0 0 0 1px rgba(0,0,0,0.08)" : "none",
              }}
            />
          ))}
          <label
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, cursor: "pointer", color: "var(--pg-text-mute)" }}
            data-testid={`${testIdPrefix}-color-custom`}
          >
            <span>Custom:</span>
            <input
              type="color"
              value={bg}
              onChange={(e) => set({ bg_color: e.target.value })}
              style={{ width: 26, height: 26, padding: 0, border: "1px solid var(--pg-border)", borderRadius: 6, cursor: "pointer" }}
              data-testid={`${testIdPrefix}-color-custom-input`}
            />
          </label>
        </div>
      </div>

      {/* Live preview */}
      <div>
        <div className="text-[11px] mb-1" style={{ color: "var(--pg-text-mute)" }}>Preview</div>
        <VideoBlockRender video={v} compact />
      </div>
    </div>
  );
}

function SegBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        padding: "5px 12px", borderRadius: 999, border: "none", cursor: "pointer",
        fontSize: 12, fontWeight: active ? 600 : 400,
        background: active ? "var(--pg-surface)" : "transparent",
        color: active ? "var(--pg-text)" : "var(--pg-text-mute)",
      }}
    >{children}</button>
  );
}

/**
 * Pure render of a video block / video page. Driven entirely by the config
 * dict so the editor's live preview, builder preview, and public pages
 * never fall out of sync.
 */
export function VideoBlockRender({ video, compact = false }) {
  if (!video) return null;
  const parsed = parseVideoEmbed(video.embed_url || "");
  const bg = video.bg_color || "#5B4FE8";
  const light = isLightColor(bg);
  const text = light ? "#0f172a" : "#ffffff";
  const subText = light ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.86)";
  const layout = video.layout === "video_only" ? "video_only" : "with_text";

  return (
    <div
      style={{
        background: bg,
        color: text,
        borderRadius: 14,
        padding: compact ? "22px 20px" : "40px 36px",
        boxShadow: light ? "inset 0 0 0 1px rgba(0,0,0,0.08)" : "none",
      }}
      data-testid="video-block-render"
    >
      {layout === "with_text" && (
        <>
          {video.title && (
            <h3
              style={{
                margin: 0,
                fontSize: compact ? 18 : 24,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: text,
              }}
              data-testid="video-block-title"
            >{video.title}</h3>
          )}
          {video.intro && (
            <p
              style={{
                margin: video.title ? "8px 0 18px" : "0 0 18px",
                color: subText,
                fontSize: compact ? 13 : 15,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                maxWidth: 720,
              }}
              data-testid="video-block-intro"
            >{video.intro}</p>
          )}
        </>
      )}

      <div
        style={{
          position: "relative",
          width: "100%",
          borderRadius: 10,
          overflow: "hidden",
          aspectRatio: "16 / 9",
          background: "#000",
          boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
        }}
      >
        {parsed ? (
          <iframe
            src={parsed.embed}
            title="Proposal video"
            data-testid="video-block-iframe"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
          />
        ) : (
          <div
            style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 10, color: "rgba(255,255,255,0.78)", fontSize: 13,
            }}
          >
            <VideoIcon size={16} />
            <span>Paste a YouTube / Vimeo / Loom / Wistia link to preview the video.</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function defaultVideoContent() {
  return {
    embed_url: "",
    title: "",
    intro: "",
    bg_color: "#5B4FE8",
    layout: "with_text",
  };
}
